import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import {
  getX, hasDateValues, daysToDate, smartYMin,
  renderXAxisLabel, xTickHeight,
  formatChartNumber, xLabelDisplay,
} from './chartUtils';

function ScatterRenderer({ data, width, height, xLabel, yLabel }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0 || !width || !height) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const isDate = hasDateValues(data);
    // Dynamic bottom margin: tall enough to fit the rotated tick labels
    // plus the X-axis title just below them — matches the LineChart pattern.
    const rawLabels = data.map((d) => String(getX(d)));
    const showX = xLabelDisplay(rawLabels);
    const tickH = xTickHeight(rawLabels.map(showX), 11, true);
    const marginBottom = Math.max(60, tickH + (xLabel ? 24 : 8));
    const margin = { top: 20, right: 20, bottom: marginBottom, left: isDate ? 110 : 70 };
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;
    const g = svg.attr('width', width).attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const yMin = isDate ? d3.min(data, (d) => d.value) * 0.95 : smartYMin(data);
    const yMax = d3.max(data, (d) => d.value) * 1.05;
    const y = d3.scaleLinear().domain([yMin, yMax]).range([h, 0]);

    // A true scatter plots numeric x on a LINEAR axis — treating a numeric x as a
    // category (scaleBand) collapsed distinct values onto evenly-spaced ticks
    // (PRD-1370 #12). Fall back to a band scale only for genuinely categorical x.
    const xNums = data.map((d) => Number(getX(d)));
    const xNumeric = xNums.every((v) => Number.isFinite(v));
    let cx;
    if (xNumeric) {
      const xScale = d3.scaleLinear().domain(d3.extent(xNums)).nice().range([0, w]);
      cx = (d) => xScale(Number(getX(d)));
      g.append('g').attr('transform', `translate(0,${h})`).call(d3.axisBottom(xScale).ticks(6).tickFormat(formatChartNumber))
        .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');
    } else {
      const xBand = d3.scaleBand().domain(data.map((d) => getX(d))).range([0, w]).padding(0.3);
      cx = (d) => xBand(getX(d)) + xBand.bandwidth() / 2;
      g.append('g').attr('transform', `translate(0,${h})`).call(d3.axisBottom(xBand).tickFormat(showX))
        .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif')
        .attr('transform', 'rotate(-45)').attr('text-anchor', 'end').attr('dx', '-0.3em').attr('dy', '0.3em')
        // Full label on hover — a truncated/rotated tick otherwise has no way to be read (PRD-1370 #20).
        .append('title').text(showX);
    }

    const yAxisFormat = isDate ? (v) => daysToDate(v) : formatChartNumber;
    g.append('g').call(d3.axisLeft(y).ticks(5).tickFormat(yAxisFormat))
      .selectAll('text').attr('font-size', '11px').attr('font-family', 'Roboto, sans-serif');

    // One consistent colour: each point is an observation, not its own category, so
    // a per-point rainbow is misleading (PRD-1370 #19 spirit). A per-datum override wins.
    const DOT_COLOR = '#4A90D9';
    g.selectAll('circle').data(data).enter().append('circle')
      .attr('cx', cx)
      .attr('cy', (d) => y(d.value))
      .attr('r', 6)
      .attr('fill', (d) => d.color || DOT_COLOR)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5)
      .append('title').text((d) => `${showX(getX(d))}: ${isDate ? daysToDate(d.value) : formatChartNumber(d.value)}`);

    // No per-point value labels: a scatter can carry hundreds of points and the
    // labels overlap into noise — the hover tooltip on each dot carries the value.

    renderXAxisLabel(g, xLabel, w, h, tickH);
    if (yLabel) {
      g.append('text').attr('transform', 'rotate(-90)')
        .attr('x', -h / 2).attr('y', -margin.left + 15)
        .attr('text-anchor', 'middle').attr('font-size', '12px')
        .attr('font-family', 'Roboto, sans-serif').attr('fill', '#757575')
        .text(yLabel);
    }
  }, [data, width, height, xLabel, yLabel]);

  return <svg ref={svgRef} />;
}

export default ScatterRenderer;
